/**
 * Your health profile — the clinical data that feeds pattern detection, cycle
 * prediction, differentials, and treatment surfaces. Distinct from /patient/settings
 * (display name / notifications / theme / account), which is app-level preferences.
 *
 * IA: a lean primary scroll (About you → Cycle → Medications) with an accordion
 * for the deep clinical history (medical, obstetrical, hormonal, urinary, family,
 * perimenopause, treatments, coping). One sticky save bar appears only when the
 * form is dirty — no per-section save buttons.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useMotionValueEvent } from 'motion/react'
import { useProfile, useSaveProfile, type ProfileData } from '@pelvia/shared/hooks/patient/useProfile'
import { useCyclePredictor, type CyclePeriodRange } from '@pelvia/shared/hooks/patient/useCyclePredictor'
import MedicationsCard from './MedicationsCard'
import CustomSelect from '@/components/CustomSelect'

const SPRING = { type: 'spring' as const, stiffness: 260, damping: 26 }

const WHAT_BRINGS_YOU = [
  ['painful_periods', 'Painful periods'],
  ['irregular_cycles', 'Irregular cycles'],
  ['pelvic_pain', 'Pelvic pain'],
  ['hormonal_symptoms', 'Hormonal symptoms'],
  ['fertility_concerns', 'Fertility concerns'],
  ['perimenopause_symptoms', 'Perimenopause symptoms'],
  ['have_diagnosis', 'I have a diagnosis'],
  ['just_starting', 'Just starting to track'],
  ['not_sure', 'Not sure yet'],
] as const

const CYCLE_PATTERNS = [
  ['regular', 'Regular (21–35 days)'],
  ['irregular', 'Irregular (varies >7 days)'],
  ['absent', 'Absent (3+ months)'],
  ['very_frequent', 'Very frequent (<21 days)'],
  ['hormonal_suppression', 'On hormonal suppression'],
  ['unsure', 'Unsure'],
] as const

const AVG_CYCLE_LEN = [
  ['under_21', 'Under 21 days'],
  ['21_35', '21–35 days'],
  ['35_60', '35–60 days'],
  ['60_plus', '60+ days'],
  ['unpredictable', 'Unpredictable'],
  ['unsure', 'Unsure'],
] as const

const PRIOR_DIAGNOSES = [
  ['endometriosis', 'Endometriosis'],
  ['pcos', 'PMOS'],
  ['adenomyosis', 'Adenomyosis'],
  ['vaginismus', 'Vaginismus'],
  ['chronic_pelvic_pain', 'Chronic pelvic pain'],
  ['interstitial_cystitis', 'Interstitial cystitis'],
  ['fibromyalgia', 'Fibromyalgia'],
  ['ibs', 'IBS'],
  ['lupus', 'Lupus'],
  ['sjogrens', 'Sjögren\u2019s'],
  ['rheumatoid_arthritis', 'Rheumatoid arthritis'],
  ['hashimotos', 'Hashimoto\u2019s'],
  ['depression', 'Depression'],
  ['hypertension', 'Hypertension'],
  ['eating_disorder', 'Eating disorder'],
  ['other', 'Other'],
] as const

const PROCEDURES = [
  ['laparoscopy', 'Laparoscopy'],
  ['excision_surgery', 'Excision surgery'],
  ['ablation', 'Ablation'],
  ['hysterectomy', 'Hysterectomy'],
  ['myomectomy', 'Myomectomy'],
  ['oophorectomy', 'Oophorectomy'],
  ['salpingectomy', 'Salpingectomy'],
  ['pelvic_floor_pt', 'Pelvic floor PT'],
  ['none_yet', 'None yet'],
  ['other', 'Other'],
] as const

const CURRENT_PROVIDER = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['looking', 'Looking for one'],
] as const

const HORMONAL_SYMPTOMS = [
  ['acne', 'Acne / skin breakouts'],
  ['excess_hair', 'Excess facial or body hair'],
  ['hair_thinning', 'Hair thinning or loss'],
  ['weight_gain', 'Unexplained weight gain'],
  ['difficulty_losing_weight', 'Difficulty losing weight'],
  ['skin_darkening', 'Skin darkening (neck, underarms)'],
  ['skin_tags', 'Skin tags'],
  ['fatigue', 'Fatigue'],
  ['mood_changes', 'Mood changes / depression'],
  ['sleep_disturbances', 'Sleep disturbances'],
  ['bloating', 'Bloating'],
  ['none', 'None of these'],
] as const

const METABOLIC_MARKERS = [
  ['elevated_testosterone', 'Elevated testosterone'],
  ['insulin_resistance', 'Insulin resistance'],
  ['elevated_blood_sugar', 'Elevated blood sugar'],
  ['high_cholesterol', 'High cholesterol'],
  ['thyroid_issues', 'Thyroid issues'],
  ['none_not_tested', 'None / not tested'],
] as const

const ULTRASOUND_PCOS = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['unsure', 'Unsure'],
] as const

const URINARY_SYMPTOMS = [
  ['stress_incontinence', 'Lose urine with cough/laugh/sneeze'],
  ['difficulty_passing', 'Difficulty passing urine'],
  ['frequent_infections', 'Frequent bladder infections'],
  ['blood_in_urine', 'Blood in urine'],
  ['incomplete_void', 'Incomplete sensation after voiding'],
  ['frequent_voids', 'Frequent voids'],
  ['none', 'None'],
] as const

const OB_COMPLICATIONS = [
  ['3rd_degree_laceration', '3rd degree laceration'],
  ['4th_degree_laceration', '4th degree laceration'],
  ['episiotomy', 'Episiotomy'],
  ['c_section', 'C-section'],
  ['vaginal_delivery', 'Vaginal delivery'],
  ['vacuum_delivery', 'Vacuum delivery'],
  ['forceps', 'Forceps'],
  ['postpartum_hemorrhage', 'Postpartum hemorrhage'],
  ['vaginal_laceration', 'Vaginal laceration'],
  ['medication_for_bleeding', 'Medication for bleeding'],
  ['other', 'Other'],
] as const

// Physical / non-medication treatments only. Medications (BCP, hormonal IUD,
// NSAIDs, etc.) are captured in MedicationsCard, so we don't duplicate them
// here — this keeps the profile focused on body-based approaches.
const PAST_TREATMENTS = [
  ['physical_therapy', 'Physical therapy'],
  ['pelvic_floor_pt', 'Pelvic floor PT'],
  ['acupuncture', 'Acupuncture'],
  ['chiropractic', 'Chiropractic'],
  ['massage', 'Massage therapy'],
  ['yoga', 'Yoga'],
  ['dietary_changes', 'Dietary changes'],
  ['therapy_counseling', 'Therapy / counseling'],
  ['tens_unit', 'TENS unit'],
  ['heat_therapy', 'Heat therapy'],
  ['other', 'Other'],
  ['nothing_yet', 'Nothing yet'],
  ['prefer_not_to_say', 'Prefer not to say'],
] as const

const EXERCISE_FREQ = [
  ['rarely', 'Rarely'],
  ['1_2_weekly', '1–2x weekly'],
  ['3_5_weekly', '3–5x weekly'],
  ['daily', 'Daily'],
] as const

const DIET = [
  ['well_balanced', 'Well balanced'],
  ['vegan', 'Vegan'],
  ['vegetarian', 'Vegetarian'],
  ['carnivore', 'Carnivore'],
  ['keto', 'Keto'],
  ['pescatarian', 'Pescatarian'],
  ['fried_food', 'Fried food'],
  ['special_diet', 'Special diet'],
  ['other', 'Other'],
] as const

const SMOKING = [
  ['never', 'Never'],
  ['former', 'Former'],
  ['current', 'Current'],
] as const

const ALCOHOL = [
  ['never', 'Never'],
  ['occasionally', 'Occasionally'],
  ['weekly', 'Weekly'],
] as const

const DRUG_USE = [
  ['never', 'Never'],
  ['used_in_past', 'Used in past'],
  ['currently_using', 'Currently using'],
  ['prefer_not_to_say', 'Prefer not to say'],
] as const

const DRUG_SUBSTANCES = [
  ['marijuana', 'Marijuana'],
  ['amphetamines', 'Amphetamines'],
  ['cocaine', 'Cocaine'],
  ['heroin', 'Heroin'],
  ['barbiturates', 'Barbiturates'],
  ['other', 'Other'],
] as const

const EDUCATION = [
  ['less_than_12', 'Less than 12 years'],
  ['high_school', 'High school / GED'],
  ['certificate', 'Certificate (2yr)'],
  ['college', 'College degree'],
  ['postgraduate', 'Postgraduate degree'],
] as const

const RACE = [
  ['', 'Prefer not to say'],
  ['american_indian', 'American Indian or Alaska Native'],
  ['asian', 'Asian'],
  ['black', 'Black or African American'],
  ['hispanic', 'Hispanic or Latino'],
  ['native_hawaiian', 'Native Hawaiian or Pacific Islander'],
  ['white', 'White'],
  ['middle_eastern', 'Middle Eastern or North African'],
  ['multiracial', 'Multiracial'],
  ['other', 'Other'],
] as const

const PRONOUNS = [
  ['she_her', 'She/her'],
  ['they_them', 'They/them'],
  ['he_him', 'He/him'],
  ['other', 'Other'],
  ['prefer_not_to_say', 'Prefer not to say'],
] as const

const COPING_SUPPORT = [
  ['spouse_partner', 'Spouse / Partner'],
  ['relative', 'Relative'],
  ['friend', 'Friend'],
  ['support_group', 'Support group'],
  ['doctor_nurse', 'Doctor / Nurse'],
  ['mental_health', 'Mental health provider'],
  ['clergy', 'Clergy'],
  ['manage_own', 'I manage on my own'],
] as const

const FAMILY_HISTORY = [
  ['endometriosis', 'Endometriosis'],
  ['cancer_breast', 'Breast cancer'],
  ['cancer_ovarian', 'Ovarian cancer'],
  ['cancer_endometrial', 'Endometrial / uterine cancer'],
  ['cancer_colon', 'Colon cancer'],
  ['cancer_pancreatic', 'Pancreatic cancer'],
  ['cancer_other', 'Other cancer'],
  ['vte', 'Blood clots / DVT / PE (VTE)'],
  ['pcos', 'PMOS'],
  ['diabetes', 'Diabetes / insulin resistance'],
  ['rheumatoid_arthritis', 'Rheumatoid arthritis'],
  ['ibs', 'IBS'],
  ['crohns_uc', 'Crohn\u2019s / UC'],
  ['lupus', 'Lupus'],
  ['obesity', 'Obesity'],
  ['none', 'None of the above'],
] as const

const PERIMENOPAUSE_SYMPTOMS = [
  ['hot_flashes', 'Hot flashes or night sweats'],
  ['sleep_changes', 'Sleep changes'],
  ['mood_anxiety', 'Mood changes or anxiety'],
  ['brain_fog', 'Brain fog'],
  ['vaginal_dryness', 'Vaginal dryness'],
  ['cycle_changes', 'Changes in cycle length or flow'],
  ['joint_pain', 'Joint pain'],
  ['heart_palpitations', 'Heart palpitations'],
  ['none', 'None yet'],
] as const

const PERIMENOPAUSE_DISCUSSED = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['unsure', 'I\u2019m not sure if this is what I\u2019m experiencing'],
] as const

function ageFromDOB(dob?: string | null): number | null {
  if (!dob) return null
  const d = new Date(dob)
  if (Number.isNaN(d.getTime())) return null
  const diff = Date.now() - d.getTime()
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000))
}

export default function ProfilePage() {
  const { data, isLoading } = useProfile()
  const save = useSaveProfile()
  const { data: cycleData } = useCyclePredictor()

  const [draft, setDraft] = useState<ProfileData>({})
  const [baseline, setBaseline] = useState<ProfileData>({})

  useEffect(() => {
    if (data?.profile) {
      setDraft(data.profile)
      setBaseline(data.profile)
    }
  }, [data])

  const patch = useCallback((updates: Partial<ProfileData>) => {
    setDraft((prev) => ({ ...prev, ...updates }))
  }, [])

  const toggleArray = useCallback(
    (key: keyof ProfileData, value: string) => {
      setDraft((prev) => {
        const cur = (prev[key] as string[] | undefined) || []
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
        return { ...prev, [key]: next }
      })
    },
    [],
  )

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  )

  // Prefer explicit age, fall back to derived-from-DOB for legacy rows.
  const age = draft.age ?? ageFromDOB(draft.date_of_birth)
  const showPerimenopause = age != null && age >= 35
  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSave = async () => {
    // Guard: if they selected "Other" as a prior diagnosis but left the detail
    // blank, a follow-up question is more useful than silently saving "other".
    const priors = draft.prior_diagnoses || []
    if (priors.includes('other') && !(draft.prior_diagnoses_other || '').trim()) {
      setSaveError(
        'Could you describe the "Other" prior diagnosis in a few words? It helps us tailor your insights.',
      )
      const el = document.querySelector<HTMLInputElement>('[data-field="prior-diagnoses-other"]')
      el?.focus()
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setSaveError(null)
    await save.mutateAsync(draft)
    setBaseline(draft)
  }

  if (isLoading) return <div className="patient-loading">Loading…</div>

  return (
    <div className="profile-page">
      <header className="profile-header">
        <h1>Your health profile</h1>
        <p>
          The clinical picture that powers your pattern detection and cycle insights.
          Update any time — changes are used immediately.
        </p>
      </header>

      {/* ===== About you ===== */}
      <section className="card">
        <div className="card-header">
          <span className="card-title">About you</span>
        </div>
        <div className="profile-grid-2">
          <div className="form-field">
            <label className="form-label">Age</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              placeholder="e.g. 28"
              style={{ maxWidth: 120 }}
              value={draft.age == null ? '' : String(draft.age)}
              onChange={(e) =>
                patch({ age: e.target.value ? parseInt(e.target.value, 10) : null })
              }
            />
          </div>
          <div className="form-field">
            <label className="form-label">Pronouns</label>
            <SingleSelect
              options={PRONOUNS}
              value={draft.pronouns || null}
              onChange={(v) => patch({ pronouns: v })}
            />
          </div>
        </div>
        <div className="profile-grid-2">
          <div className="form-field">
            <label className="form-label">Height</label>
            <HeightInput
              value={draft.height || ''}
              onChange={(v) => patch({ height: v })}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Weight</label>
            <WeightInput
              value={draft.weight || ''}
              onChange={(v) => patch({ weight: v })}
            />
          </div>
        </div>
      </section>

      {/* ===== Cycle ===== */}
      <section className="card" id="cycle-history">
        <div className="card-header">
          <span className="card-title">Cycle</span>
          <span className="card-header-sub">Feeds your cycle predictor + phase charts</span>
        </div>
        <div className="profile-grid-2">
          <div className="form-field">
            <label className="form-label">Cycle length (days)</label>
            <input
              className="form-input"
              type="number"
              min={18}
              max={60}
              placeholder="28"
              value={draft.cycle_length_days ?? ''}
              onChange={(e) =>
                patch({
                  cycle_length_days: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
            />
          </div>
          <div className="form-field">
            <label className="form-label">Last period start</label>
            <RecentDatePicker
              value={draft.last_period_start || null}
              onChange={(v) => patch({ last_period_start: v })}
            />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Recent period ranges</label>
          <div className="form-hint" style={{ marginBottom: 10 }}>
            Tap a start day, then an end day. Add as many recent cycles as you remember — more
            data sharpens your cycle predictions and goes straight into your doctor report.
          </div>
          <LmpRangeEditor
            ranges={draft.lmp_ranges || []}
            detectedRanges={cycleData?.detected_period_ranges || []}
            predictedRanges={cycleData?.predicted_period_ranges || []}
            onChange={(next) => {
              const sorted = [...next].sort((a, b) => b.start.localeCompare(a.start))
              const newestStart = sorted[0]?.start || null
              patch({
                lmp_ranges: sorted,
                last_period_start: newestStart ?? draft.last_period_start ?? null,
              })
            }}
          />
        </div>
        <div className="form-field">
          <label className="form-label">What brings you to Pelvia?</label>
          <MultiSelect
            options={WHAT_BRINGS_YOU}
            values={draft.what_brings_you || []}
            onToggle={(v) => toggleArray('what_brings_you', v)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Cycle pattern</label>
          <SingleSelect
            options={CYCLE_PATTERNS}
            value={draft.cycle_pattern || null}
            onChange={(v) => patch({ cycle_pattern: v === 'unsure' ? null : v })}
          />
        </div>
        {draft.cycle_pattern === 'irregular' && (
          <div className="form-field">
            <label className="form-label">Average cycle length</label>
            <SingleSelect
              options={AVG_CYCLE_LEN}
              value={draft.avg_cycle_length_range || null}
              onChange={(v) => patch({ avg_cycle_length_range: v === 'unsure' ? null : v })}
            />
          </div>
        )}
      </section>

      {/* ===== Medications (pinned above accordion — highest edit frequency) ===== */}
      <MedicationsCard
        conditions={[
          ...(draft.prior_diagnoses || []),
          ...(draft.what_brings_you || []),
        ]}
      />

      {/* ===== Care team ===== */}
      <Accordion title="Care team">
        <div className="form-field">
          <label className="form-label">GYN / specialist</label>
          <input
            className="form-input"
            placeholder="Dr. Smith"
            value={draft.gyn_name || ''}
            onChange={(e) => patch({ gyn_name: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Practice / hospital</label>
          <input
            className="form-input"
            placeholder="City Women’s Health"
            value={draft.gyn_practice || ''}
            onChange={(e) => patch({ gyn_practice: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            MIGS/REI specialist <span className="form-hint">(optional)</span>
          </label>
          <input
            className="form-input"
            value={draft.migs_specialist || ''}
            onChange={(e) => patch({ migs_specialist: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            Other providers <span className="form-hint">(one per line)</span>
          </label>
          <textarea
            className="form-input"
            rows={2}
            placeholder="PCP, physical therapist, etc."
            value={(draft.other_providers || []).map((p) => p.name).join('\n')}
            onChange={(e) =>
              patch({
                other_providers: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((name) => ({ name })),
              })
            }
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            Anything else we should know? <span className="form-hint">(health context only)</span>
          </label>
          <input
            className="form-input"
            value={draft.anything_else || ''}
            onChange={(e) => patch({ anything_else: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Med allergies</label>
          <input
            className="form-input"
            placeholder="e.g. Penicillin, latex"
            value={draft.med_allergies || ''}
            onChange={(e) => patch({ med_allergies: e.target.value })}
          />
        </div>
      </Accordion>

      {/* ===== Health history accordion ===== */}
      <div className="profile-history-heading">
        <h2>Health history</h2>
        <p>Deeper context that sharpens differentials and recommendations.</p>
      </div>

      <Accordion title="Medical history" defaultOpen={false}>
        <div className="form-field">
          <label className="form-label">
            Prior diagnoses <span className="form-hint">(select all that apply)</span>
          </label>
          <MultiSelect
            options={PRIOR_DIAGNOSES}
            values={draft.prior_diagnoses || []}
            onToggle={(v) => toggleArray('prior_diagnoses', v)}
          />
        </div>
        {(draft.prior_diagnoses || []).includes('other') && (
          <div className="form-field">
            <label className="form-label">
              Please specify <span className="form-hint">(required)</span>
            </label>
            <input
              className="form-input"
              data-field="prior-diagnoses-other"
              placeholder="e.g. vulvodynia, PMDD, pudendal neuralgia"
              value={draft.prior_diagnoses_other || ''}
              onChange={(e) => {
                patch({ prior_diagnoses_other: e.target.value })
                if (saveError) setSaveError(null)
              }}
            />
          </div>
        )}
        <div className="form-field">
          <label className="form-label">
            Surgical history <span className="form-hint">(select all that apply)</span>
          </label>
          <MultiSelect
            options={PROCEDURES}
            values={draft.procedures || []}
            onToggle={(v) => toggleArray('procedures', v)}
          />
        </div>
        {(draft.procedures || []).includes('other') && (
          <div className="form-field">
            <label className="form-label">Please specify</label>
            <input
              className="form-input"
              value={draft.procedures_other || ''}
              onChange={(e) => patch({ procedures_other: e.target.value })}
            />
          </div>
        )}
        <div className="form-field">
          <label className="form-label">Currently being treated by a provider?</label>
          <SingleSelect
            options={CURRENT_PROVIDER}
            value={draft.current_provider || null}
            onChange={(v) => patch({ current_provider: v })}
          />
        </div>
      </Accordion>

      <Accordion title="Hormonal & metabolic">
        <div className="form-field">
          <label className="form-label">
            Current symptoms <span className="form-hint">(select all that apply)</span>
          </label>
          <MultiSelect
            options={HORMONAL_SYMPTOMS}
            values={draft.hormonal_symptoms || []}
            onToggle={(v) => toggleArray('hormonal_symptoms', v)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            Metabolic markers <span className="form-hint">(optional)</span>
          </label>
          <MultiSelect
            options={METABOLIC_MARKERS}
            values={draft.metabolic_markers || []}
            onToggle={(v) => toggleArray('metabolic_markers', v)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            Ultrasound indicating PMOS ovaries or high follicle count?
          </label>
          <SingleSelect
            options={ULTRASOUND_PCOS}
            value={draft.ultrasound_pcos || null}
            onChange={(v) => patch({ ultrasound_pcos: v })}
          />
        </div>
      </Accordion>

      <Accordion title="Urinary history">
        <p className="accordion-hint">
          Ongoing issues only — for today’s symptoms, use the daily check-in.
        </p>
        <div className="form-field">
          <label className="form-label">Select any that apply</label>
          <MultiSelect
            options={URINARY_SYMPTOMS}
            values={draft.urinary_symptoms || []}
            onToggle={(v) => toggleArray('urinary_symptoms', v)}
          />
        </div>
      </Accordion>

      <Accordion title="Obstetrical history">
        <div className="form-field">
          <label className="form-label">Have you ever been pregnant?</label>
          <SingleSelect
            options={[['yes', 'Yes'], ['no', 'No']]}
            value={
              draft.pregnancies_count == null
                ? null
                : draft.pregnancies_count > 0
                ? 'yes'
                : 'no'
            }
            onChange={(v) => {
              // "No" pins the count at 0 and wipes any stale details. "Yes"
              // clears the count so the follow-up input reads as empty, not 0.
              if (v === 'no') {
                patch({
                  pregnancies_count: 0,
                  full_term: null,
                  premature: null,
                  miscarriage_abortion: null,
                  living_children: null,
                  ob_complications: [],
                })
              } else {
                patch({ pregnancies_count: null })
              }
            }}
          />
        </div>
        {(draft.pregnancies_count ?? 0) > 0 ||
        (draft.pregnancies_count == null &&
          ((draft.full_term ?? 0) +
            (draft.premature ?? 0) +
            (draft.miscarriage_abortion ?? 0)) >
            0) ? (
          <div className="form-field">
            <label className="form-label">How many pregnancies?</label>
            <input
              className="form-input"
              type="number"
              min={1}
              style={{ maxWidth: 100 }}
              value={draft.pregnancies_count ?? ''}
              onChange={(e) =>
                patch({
                  pregnancies_count: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
            />
          </div>
        ) : null}
        {(draft.pregnancies_count ?? 0) > 0 && (
          <>
            <div className="profile-grid-2">
              <div className="form-field">
                <label className="form-label">Full term</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={draft.full_term ?? ''}
                  onChange={(e) =>
                    patch({ full_term: e.target.value ? parseInt(e.target.value, 10) : null })
                  }
                />
              </div>
              <div className="form-field">
                <label className="form-label">Premature</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={draft.premature ?? ''}
                  onChange={(e) =>
                    patch({ premature: e.target.value ? parseInt(e.target.value, 10) : null })
                  }
                />
              </div>
              <div className="form-field">
                <label className="form-label">Miscarriage / abortion</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={draft.miscarriage_abortion ?? ''}
                  onChange={(e) =>
                    patch({
                      miscarriage_abortion: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    })
                  }
                />
              </div>
              <div className="form-field">
                <label className="form-label">Living children</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={draft.living_children ?? ''}
                  onChange={(e) =>
                    patch({
                      living_children: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                />
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">
                Complications <span className="form-hint">(select all that apply)</span>
              </label>
              <MultiSelect
                options={OB_COMPLICATIONS}
                values={draft.ob_complications || []}
                onToggle={(v) => toggleArray('ob_complications', v)}
              />
            </div>
          </>
        )}
      </Accordion>

      <Accordion title="Non-medication treatments">
        <p className="accordion-hint">
          Physical, body-based, and lifestyle approaches. Medications live in the
          Medications section above so we don't ask twice.
        </p>
        <div className="form-field">
          <label className="form-label">
            Tried or currently using <span className="form-hint">(select all that apply)</span>
          </label>
          <MultiSelect
            options={PAST_TREATMENTS}
            values={draft.past_treatments || []}
            onToggle={(v) => toggleArray('past_treatments', v)}
          />
        </div>
        {(draft.past_treatments || []).includes('other') && (
          <div className="form-field">
            <label className="form-label">Please specify</label>
            <input
              className="form-input"
              value={draft.past_treatments_other || ''}
              onChange={(e) => patch({ past_treatments_other: e.target.value })}
            />
          </div>
        )}
      </Accordion>

      <Accordion title="Lifestyle">
        <div className="form-field">
          <label className="form-label">Exercise frequency</label>
          <SingleSelect
            options={EXERCISE_FREQ}
            value={draft.exercise_frequency || null}
            onChange={(v) => patch({ exercise_frequency: v })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">
            Diet <span className="form-hint">(select all that apply)</span>
          </label>
          <MultiSelect
            options={DIET}
            values={draft.diet || []}
            onToggle={(v) => toggleArray('diet', v)}
          />
        </div>
        {(draft.diet || []).includes('special_diet') && (
          <div className="form-field">
            <input
              className="form-input"
              placeholder="Describe your diet"
              value={draft.diet_special || ''}
              onChange={(e) => patch({ diet_special: e.target.value })}
            />
          </div>
        )}
        <div className="form-field">
          <label className="form-label">Smoking</label>
          <SingleSelect
            options={SMOKING}
            value={draft.smoking_status || null}
            onChange={(v) => patch({ smoking_status: v })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Alcohol</label>
          <SingleSelect
            options={ALCOHOL}
            value={draft.alcohol_status || null}
            // A per-week count only makes sense for regular drinkers, so drop any
            // stale value when switching away from "weekly" (see the guarded field below).
            onChange={(v) =>
              patch(v === 'weekly' ? { alcohol_status: v } : { alcohol_status: v, alcohol_drinks_week: null })
            }
          />
        </div>
        {/* Only ask a weekly quantity of weekly drinkers — "drinks per week" is an
            odd question for someone who drinks occasionally (e.g. once a month). */}
        {draft.alcohol_status === 'weekly' && (
          <div className="form-field">
            <label className="form-label">
              Drinks per week <span className="form-hint">(optional)</span>
            </label>
            <input
              className="form-input"
              type="number"
              min={0}
              style={{ maxWidth: 120 }}
              value={draft.alcohol_drinks_week ?? ''}
              onChange={(e) =>
                patch({
                  alcohol_drinks_week: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
            />
          </div>
        )}
        <div className="form-field">
          <label className="form-label">Recreational drug use</label>
          <SingleSelect
            options={DRUG_USE}
            value={draft.drug_use_status || null}
            onChange={(v) => patch({ drug_use_status: v })}
          />
        </div>
        {draft.drug_use_status === 'currently_using' || draft.drug_use_status === 'used_in_past' ? (
          <div className="form-field">
            <label className="form-label">Substances</label>
            <MultiSelect
              options={DRUG_SUBSTANCES}
              values={draft.drug_use_substances || []}
              onToggle={(v) => toggleArray('drug_use_substances', v)}
            />
          </div>
        ) : null}
      </Accordion>

      <Accordion title="Family history">
        <div className="form-field">
          <label className="form-label">
            Any family history of the following? <span className="form-hint">(select all)</span>
          </label>
          <MultiSelect
            options={FAMILY_HISTORY}
            values={draft.family_history || []}
            onToggle={(v) => toggleArray('family_history', v)}
          />
        </div>
      </Accordion>

      <Accordion title="Coping & support">
        <div className="form-field">
          <label className="form-label">
            Who do you talk to during stressful times?{' '}
            <span className="form-hint">(select all)</span>
          </label>
          <MultiSelect
            options={COPING_SUPPORT}
            values={draft.coping_support || []}
            onToggle={(v) => toggleArray('coping_support', v)}
          />
        </div>
      </Accordion>

      {showPerimenopause && (
        <Accordion title="Perimenopause" defaultOpen={true}>
          <div className="form-field">
            <label className="form-label">
              Are you experiencing any of the following?{' '}
              <span className="form-hint">(select all)</span>
            </label>
            <MultiSelect
              options={PERIMENOPAUSE_SYMPTOMS}
              values={draft.perimenopause_symptoms || []}
              onToggle={(v) => toggleArray('perimenopause_symptoms', v)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Have you discussed perimenopause with a provider?</label>
            <SingleSelect
              options={PERIMENOPAUSE_DISCUSSED}
              value={draft.perimenopause_discussed || null}
              onChange={(v) => patch({ perimenopause_discussed: v })}
            />
          </div>
        </Accordion>
      )}

      <Accordion title="Demographics">
        <p className="accordion-hint">Optional, used to calibrate predictions across populations.</p>
        <div className="form-field">
          <label className="form-label">Race / ethnicity</label>
          <CustomSelect
            options={RACE}
            value={draft.race_ethnicity || null}
            onChange={(v) => patch({ race_ethnicity: v })}
            placeholder="Select…"
            ariaLabel="Race / ethnicity"
          />
        </div>
        <div className="form-field">
          <label className="form-label">Highest level of education</label>
          <SingleSelect
            options={EDUCATION}
            value={draft.education || null}
            onChange={(v) => patch({ education: v })}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Type of work</label>
          <input
            className="form-input"
            placeholder="e.g. office, healthcare, retail"
            value={draft.work_type || ''}
            onChange={(e) => patch({ work_type: e.target.value })}
          />
        </div>
      </Accordion>

      <AnimatePresence>
        {isDirty && (
          <motion.div
            key="save-bar"
            className="profile-save-bar"
            initial={{ y: 72, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 72, opacity: 0 }}
            transition={SPRING}
          >
            <span className="profile-save-label">
              {saveError ?? 'Unsaved changes'}
            </span>
            <motion.button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={save.isPending}
              whileTap={{ scale: 0.96 }}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ----- Shared UI bits -----

function Accordion({
  title,
  defaultOpen = false,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <motion.section layout className="card accordion" transition={SPRING}>
      <button
        type="button"
        className="accordion-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="accordion-title">{title}</span>
        <motion.span
          className="accordion-chev"
          animate={{ rotate: open ? 90 : 0 }}
          transition={SPRING}
          aria-hidden
        >
          ›
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            className="accordion-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={SPRING}
            style={{ overflow: 'hidden' }}
          >
            <div className="accordion-inner">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  )
}

function SingleSelect({
  options,
  value,
  onChange,
}: {
  options: readonly (readonly [string, string])[]
  value: string | null
  onChange: (v: string | null) => void
}) {
  return (
    <div className="chip-group chip-group-single">
      {options.map(([v, label]) => (
        <motion.button
          key={v}
          type="button"
          className={`chip${value === v ? ' selected' : ''}`}
          onClick={() => onChange(value === v ? null : v)}
          whileTap={{ scale: 0.94 }}
        >
          {label}
        </motion.button>
      ))}
    </div>
  )
}

function MultiSelect({
  options,
  values,
  onToggle,
}: {
  options: readonly (readonly [string, string])[]
  values: string[]
  onToggle: (v: string) => void
}) {
  return (
    <div className="chip-group">
      {options.map(([v, label]) => (
        <motion.button
          key={v}
          type="button"
          className={`chip${values.includes(v) ? ' selected' : ''}`}
          onClick={() => onToggle(v)}
          whileTap={{ scale: 0.94 }}
        >
          {label}
        </motion.button>
      ))}
    </div>
  )
}

// ----- Date pickers -----
// RecentDatePicker uses a month-grid calendar because last-period-start is
// always within the past few weeks — calendar is a natural shape for
// "which day was that?".

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

function parseISO(s: string | null): { y: number; m: number; d: number } | null {
  if (!s) return null
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10))
  if (!y || !m || !d) return null
  return { y, m, d }
}

function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate()
}

function RecentDatePicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (v: string | null) => void
}) {
  const parsed = parseISO(value)
  const today = new Date()
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(parsed?.y ?? today.getFullYear())
  const [viewMonth, setViewMonth] = useState(parsed?.m ?? today.getMonth() + 1) // 1-indexed
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const shiftMonth = (delta: number) => {
    let nm = viewMonth + delta
    let ny = viewYear
    while (nm < 1) { nm += 12; ny -= 1 }
    while (nm > 12) { nm -= 12; ny += 1 }
    setViewMonth(nm); setViewYear(ny)
  }

  const firstDow = new Date(viewYear, viewMonth - 1, 1).getDay() // 0=Sun
  const lastDay = daysInMonth(viewYear, viewMonth)
  const cells: (number | null)[] = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= lastDay; d++) cells.push(d)

  const todayISO = toISO(today.getFullYear(), today.getMonth() + 1, today.getDate())
  const isFuture = (day: number) => toISO(viewYear, viewMonth, day) > todayISO

  const displayLabel = parsed
    ? `${MONTH_NAMES[parsed.m - 1].slice(0, 3)} ${parsed.d}, ${parsed.y}`
    : 'Pick a date'

  return (
    <div className="recent-date" ref={containerRef}>
      <motion.button
        type="button"
        className={`recent-date-trigger${parsed ? ' has-value' : ''}`}
        onClick={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.98 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span>{displayLabel}</span>
        {parsed && (
          <span
            className="recent-date-clear"
            role="button"
            aria-label="Clear date"
            onClick={(e) => { e.stopPropagation(); onChange(null) }}
          >
            ×
          </span>
        )}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="recent-date-popover"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={SPRING}
          >
            <div className="recent-date-nav">
              <button type="button" className="recent-date-navbtn" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
              <div className="recent-date-title">
                {MONTH_NAMES[viewMonth - 1]} {viewYear}
              </div>
              <button type="button" className="recent-date-navbtn" onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
            </div>
            <div className="recent-date-dow">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="recent-date-grid">
              {cells.map((day, i) => {
                if (day == null) return <span key={i} className="recent-date-cell recent-date-empty" />
                const iso = toISO(viewYear, viewMonth, day)
                const selected = parsed && iso === value
                const future = isFuture(day)
                const isToday = iso === todayISO
                return (
                  <motion.button
                    key={i}
                    type="button"
                    className={`recent-date-cell${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
                    disabled={future}
                    onClick={() => { onChange(iso); setOpen(false) }}
                    whileTap={future ? undefined : { scale: 0.88 }}
                  >
                    {day}
                  </motion.button>
                )
              })}
            </div>
            <div className="recent-date-footer">
              <button
                type="button"
                className="recent-date-today"
                onClick={() => { onChange(todayISO); setOpen(false) }}
              >
                Today
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ----- LMP range editor -----
// Post-intake editor for the same lmp_ranges[] the Intake Step 5 calendar
// captures. Kept class-compatible with .lmp-calendar styles so Intake and
// Profile look identical — tap start, tap end, tap again to remove.

function LmpRangeEditor({
  ranges,
  detectedRanges = [],
  predictedRanges = [],
  onChange,
}: Readonly<{
  ranges: { start: string; end: string }[]
  detectedRanges?: CyclePeriodRange[]
  predictedRanges?: CyclePeriodRange[]
  onChange: (next: { start: string; end: string }[]) => void
}>) {
  const [monthOffset, setMonthOffset] = useState(0)
  const [pendingStart, setPendingStart] = useState<string | null>(null)

  const viewDate = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() + monthOffset)
    return d
  }, [monthOffset])

  const monthLabel = viewDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  const dim = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate()
  const firstWeekday = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay()
  const today = new Date().toISOString().slice(0, 10)

  // Hide detected ranges that overlap an entry the user already has, so we
  // don't render the same period twice. User-entered ranges take precedence.
  const overlapsUserRange = useCallback(
    (r: { start: string; end: string }) =>
      ranges.some((u) => !(u.end < r.start || u.start > r.end)),
    [ranges],
  )
  const visibleDetected = useMemo(
    () => detectedRanges.filter((r) => !overlapsUserRange(r)),
    [detectedRanges, overlapsUserRange],
  )
  const visiblePredicted = useMemo(
    () => predictedRanges.filter((r) => !overlapsUserRange(r) && r.start > today),
    [predictedRanges, overlapsUserRange, today],
  )

  const isInRange = useCallback(
    (iso: string) => ranges.some((r) => r.start <= iso && iso <= r.end),
    [ranges],
  )
  const isDetected = useCallback(
    (iso: string) => visibleDetected.some((r) => r.start <= iso && iso <= r.end),
    [visibleDetected],
  )
  const isPredicted = useCallback(
    (iso: string) => visiblePredicted.some((r) => r.start <= iso && iso <= r.end),
    [visiblePredicted],
  )

  // Allow forward navigation through the end of the calendar year so predicted
  // periods (vacation planning) are reachable. Past navigation stays open.
  const endOfYear = `${new Date().getFullYear()}-12-01`
  const canGoForward = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-01` < endOfYear

  const onDayClick = (iso: string) => {
    if (iso > today) return
    const covering = ranges.findIndex((r) => r.start <= iso && iso <= r.end)
    if (covering !== -1) {
      onChange(ranges.filter((_, i) => i !== covering))
      return
    }
    // Tapping a detected day promotes it into the user's logged ranges so the
    // user can confirm/adjust the dates the app inferred from check-ins.
    const detectedHit = visibleDetected.find((r) => r.start <= iso && iso <= r.end)
    if (detectedHit && !pendingStart) {
      onChange([
        ...ranges.filter((r) => r.end < detectedHit.start || r.start > detectedHit.end),
        { start: detectedHit.start, end: detectedHit.end },
      ])
      return
    }
    if (!pendingStart) {
      setPendingStart(iso)
      return
    }
    const start = pendingStart < iso ? pendingStart : iso
    const end = pendingStart < iso ? iso : pendingStart
    const merged = [...ranges.filter((r) => r.end < start || r.start > end), { start, end }]
    onChange(merged)
    setPendingStart(null)
  }

  const cells: (string | null)[] = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= dim; d++) {
    const iso = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push(iso)
  }

  const fmt = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div>
      <div className="lmp-calendar">
        <div className="lmp-cal-header">
          <button
            type="button"
            className="lmp-cal-nav"
            onClick={() => setMonthOffset((v) => v - 1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="lmp-cal-month">{monthLabel}</div>
          <button
            type="button"
            className="lmp-cal-nav"
            onClick={() => setMonthOffset((v) => v + 1)}
            aria-label="Next month"
            disabled={!canGoForward}
          >
            ›
          </button>
        </div>
        <div className="lmp-cal-weekdays">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={`wd-${i}`} className="lmp-cal-weekday">{d}</div>
          ))}
        </div>
        <div className="lmp-cal-grid">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`e-${i}`} />
            const future = iso > today
            const selected = isInRange(iso)
            const detected = !selected && isDetected(iso)
            const predicted = !selected && !detected && isPredicted(iso)
            const pending = pendingStart === iso
            const cls = [
              'lmp-cal-day',
              selected && 'selected',
              detected && 'detected',
              predicted && 'predicted',
              pending && 'pending',
              future && !predicted && 'future',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <button
                key={iso}
                type="button"
                className={cls}
                onClick={() => onDayClick(iso)}
                disabled={future && !detected}
                title={
                  detected
                    ? 'Detected from your check-ins — tap to confirm and adjust'
                    : predicted
                      ? 'Predicted period (estimated)'
                      : undefined
                }
              >
                {parseInt(iso.slice(8, 10), 10)}
              </button>
            )
          })}
        </div>
        {(visibleDetected.length > 0 || visiblePredicted.length > 0) && (
          <div className="lmp-cal-legend">
            <span className="lmp-legend-item">
              <span className="lmp-legend-swatch lmp-legend-logged" /> Logged
            </span>
            {visibleDetected.length > 0 && (
              <span className="lmp-legend-item">
                <span className="lmp-legend-swatch lmp-legend-detected" /> From check-ins
              </span>
            )}
            {visiblePredicted.length > 0 && (
              <span className="lmp-legend-item">
                <span className="lmp-legend-swatch lmp-legend-predicted" /> Predicted
              </span>
            )}
          </div>
        )}
      </div>

      {ranges.length > 0 && (
        <div className="lmp-range-list">
          <div className="lmp-range-label">Logged periods:</div>
          {ranges.map((r) => (
            <div key={`${r.start}-${r.end}`} className="lmp-range-item">
              <span>
                {fmt(r.start)} – {fmt(r.end)}
              </span>
              <button
                type="button"
                aria-label="Remove period range"
                onClick={() => onChange(ranges.filter((x) => x !== r))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {visibleDetected.length > 0 && (
        <div className="lmp-range-list">
          <div className="lmp-range-label">Detected from check-ins:</div>
          {visibleDetected.map((r) => (
            <div key={`d-${r.start}-${r.end}`} className="lmp-range-item lmp-range-detected">
              <span>
                {fmt(r.start)} – {fmt(r.end)}
                {r.estimated && <em className="lmp-range-flag"> (estimated end)</em>}
              </span>
              <button
                type="button"
                aria-label="Confirm detected period range"
                title="Confirm — adds to your logged periods so you can adjust"
                onClick={() =>
                  onChange([
                    ...ranges.filter((x) => x.end < r.start || x.start > r.end),
                    { start: r.start, end: r.end },
                  ])
                }
              >
                ✓
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingStart && (
        <div className="lmp-hint">
          Start: <strong>{fmt(pendingStart)}</strong> — now tap the end day.
          <button
            type="button"
            className="lmp-hint-clear"
            onClick={() => setPendingStart(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

// ----- Height / Weight -----
// Stored as free-text (backward compatibility) but edited via a segmented
// unit toggle with numeric fields. Parses existing values on mount so users
// can re-open and tweak.

function parseHeight(value: string): { unit: 'imperial' | 'metric'; ft: string; in: string; cm: string } {
  const v = value.trim()
  const imp = v.match(/^(\d+)\s*['’]\s*(\d+)?\s*["”]?$/)
  if (imp) return { unit: 'imperial', ft: imp[1], in: imp[2] || '', cm: '' }
  const metric = v.match(/^(\d+(?:\.\d+)?)\s*cm$/i)
  if (metric) return { unit: 'metric', ft: '', in: '', cm: metric[1] }
  const bare = v.match(/^(\d+(?:\.\d+)?)$/)
  if (bare) return { unit: 'metric', ft: '', in: '', cm: bare[1] }
  return { unit: 'imperial', ft: '', in: '', cm: '' }
}

function formatHeight(unit: 'imperial' | 'metric', ft: string, inches: string, cm: string): string {
  if (unit === 'imperial') {
    if (!ft && !inches) return ''
    return `${ft || 0}'${inches || 0}"`
  }
  if (!cm) return ''
  return `${cm}cm`
}

function HeightInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const parsed = useMemo(() => parseHeight(value), [value])
  const [unit, setUnit] = useState<'imperial' | 'metric'>(parsed.unit)
  const [ft, setFt] = useState(parsed.ft)
  const [inches, setInches] = useState(parsed.in)
  const [cm, setCm] = useState(parsed.cm)

  // When the parent value changes externally (e.g. profile load), resync.
  useEffect(() => {
    const p = parseHeight(value)
    setUnit(p.unit)
    setFt(p.ft)
    setInches(p.in)
    setCm(p.cm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const push = (u: typeof unit, f: string, i: string, c: string) => {
    onChange(formatHeight(u, f, i, c))
  }

  const toggle = (u: typeof unit) => {
    if (u === unit) return
    // Convert between units so the value persists visually.
    if (u === 'metric' && (ft || inches)) {
      const totalIn = (parseInt(ft || '0', 10) * 12) + parseInt(inches || '0', 10)
      const nextCm = String(Math.round(totalIn * 2.54))
      setCm(nextCm); setUnit(u); push(u, ft, inches, nextCm); return
    }
    if (u === 'imperial' && cm) {
      const totalIn = parseFloat(cm) / 2.54
      const nf = Math.floor(totalIn / 12)
      const ni = Math.round(totalIn - nf * 12)
      setFt(String(nf)); setInches(String(ni)); setUnit(u); push(u, String(nf), String(ni), cm); return
    }
    setUnit(u); push(u, ft, inches, cm)
  }

  return (
    <div className="measure-input">
      <UnitToggle
        groupId="height"
        options={[['imperial', 'ft / in'], ['metric', 'cm']]}
        value={unit}
        onChange={(v) => toggle(v as 'imperial' | 'metric')}
      />
      <AnimatePresence mode="wait" initial={false}>
        {unit === 'imperial' ? (
          <motion.div
            key="imp"
            className="measure-fields"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            <div className="measure-field">
              <input
                className="form-input measure-number"
                type="text"
                inputMode="numeric"
                placeholder="5"
                maxLength={1}
                value={ft}
                onChange={(e) => {
                  const n = e.target.value.replace(/\D/g, '').slice(0, 1)
                  setFt(n); push(unit, n, inches, cm)
                }}
              />
              <span className="measure-suffix">ft</span>
            </div>
            <div className="measure-field">
              <input
                className="form-input measure-number"
                type="text"
                inputMode="numeric"
                placeholder="6"
                maxLength={2}
                value={inches}
                onChange={(e) => {
                  const n = e.target.value.replace(/\D/g, '').slice(0, 2)
                  setInches(n); push(unit, ft, n, cm)
                }}
              />
              <span className="measure-suffix">in</span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="met"
            className="measure-fields"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            <div className="measure-field">
              <input
                className="form-input measure-number"
                type="text"
                inputMode="numeric"
                placeholder="168"
                maxLength={3}
                value={cm}
                onChange={(e) => {
                  const n = e.target.value.replace(/[^\d.]/g, '').slice(0, 5)
                  setCm(n); push(unit, ft, inches, n)
                }}
              />
              <span className="measure-suffix">cm</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function parseWeight(value: string): { unit: 'lbs' | 'kg'; n: string } {
  const v = value.trim()
  const kg = v.match(/^(\d+(?:\.\d+)?)\s*kg$/i)
  if (kg) return { unit: 'kg', n: kg[1] }
  const lbs = v.match(/^(\d+(?:\.\d+)?)\s*(?:lb|lbs|pounds?)$/i)
  if (lbs) return { unit: 'lbs', n: lbs[1] }
  const bare = v.match(/^(\d+(?:\.\d+)?)$/)
  if (bare) return { unit: 'lbs', n: bare[1] }
  return { unit: 'lbs', n: '' }
}

// Weight is a horizontal ruler dial: user drags the strip left/right and the
// value under the center pointer is the answer. Each unit = TICK_PX pixels,
// with labelled major ticks every 10. Snaps to nearest integer on drag end.
// Arrow keys nudge +/- 1 for keyboard accessibility.

const TICK_PX = 10

function WeightInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const parsed = useMemo(() => parseWeight(value), [value])
  const [unit, setUnit] = useState<'lbs' | 'kg'>(parsed.unit)
  const defaultFor = (u: 'lbs' | 'kg') => (u === 'kg' ? 64 : 140)
  const [val, setVal] = useState<number>(
    parsed.n ? Math.round(parseFloat(parsed.n)) : defaultFor(parsed.unit),
  )

  const MIN = unit === 'kg' ? 25 : 55
  const MAX = unit === 'kg' ? 180 : 400

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = useState(0)
  const x = useMotionValue(0)
  const draggingRef = useRef(false)

  // Measure the track so we can center-align the pointer.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setContainerW(el.offsetWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const xForValue = useCallback(
    (v: number) => containerW / 2 - (v - MIN) * TICK_PX,
    [containerW, MIN],
  )

  // Recenter whenever layout or unit changes. Skip while dragging so we don't
  // yank the strip out from under the user's finger.
  useEffect(() => {
    if (containerW === 0 || draggingRef.current) return
    x.set(xForValue(val))
  }, [containerW, unit, val, x, xForValue])

  // Sync external changes (profile load / unit toggle pushing a new value).
  useEffect(() => {
    const p = parseWeight(value)
    if (p.unit !== unit) setUnit(p.unit)
    const n = p.n ? Math.round(parseFloat(p.n)) : defaultFor(p.unit)
    if (n !== val) setVal(n)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useMotionValueEvent(x, 'change', (latest) => {
    if (containerW === 0) return
    const raw = MIN + (containerW / 2 - latest) / TICK_PX
    const rounded = Math.max(MIN, Math.min(MAX, Math.round(raw)))
    if (rounded !== val) {
      setVal(rounded)
      onChange(`${rounded} ${unit}`)
    }
  })

  const toggle = (u: 'lbs' | 'kg') => {
    if (u === unit) return
    const converted = u === 'kg' ? Math.round(val / 2.2046) : Math.round(val * 2.2046)
    setUnit(u)
    setVal(converted)
    onChange(`${converted} ${u}`)
  }

  const nudge = (delta: number) => {
    const next = Math.max(MIN, Math.min(MAX, val + delta))
    setVal(next)
    onChange(`${next} ${unit}`)
    animate(x, xForValue(next), { type: 'spring', stiffness: 400, damping: 32 })
  }

  const ticks: number[] = []
  for (let v = MIN; v <= MAX; v++) ticks.push(v)

  return (
    <div className="measure-input">
      <UnitToggle
        groupId="weight"
        options={[['lbs', 'lbs'], ['kg', 'kg']]}
        value={unit}
        onChange={(v) => toggle(v as 'lbs' | 'kg')}
      />
      <div
        className="weight-dial"
        ref={containerRef}
        tabIndex={0}
        role="slider"
        aria-valuemin={MIN}
        aria-valuemax={MAX}
        aria-valuenow={val}
        aria-valuetext={`${val} ${unit}`}
        aria-label="Weight"
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); nudge(-1) }
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); nudge(1) }
        }}
      >
        <div className="weight-readout">
          <motion.span
            key={`${val}-${unit}`}
            className="weight-readout-number"
            initial={{ opacity: 0.5, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.12 }}
          >
            {val}
          </motion.span>
          <span className="weight-readout-unit">{unit}</span>
        </div>
        <div className="weight-dial-track">
          <motion.div
            className="weight-dial-strip"
            drag="x"
            style={{ x, width: (MAX - MIN) * TICK_PX }}
            dragConstraints={{
              left: containerW / 2 - (MAX - MIN) * TICK_PX,
              right: containerW / 2,
            }}
            dragElastic={0.05}
            dragMomentum={true}
            onDragStart={() => { draggingRef.current = true }}
            onDragEnd={() => {
              draggingRef.current = false
              const raw = MIN + (containerW / 2 - x.get()) / TICK_PX
              const snapped = Math.max(MIN, Math.min(MAX, Math.round(raw)))
              animate(x, xForValue(snapped), { type: 'spring', stiffness: 280, damping: 30 })
            }}
          >
            {ticks.map((v) => {
              const major = v % 10 === 0
              const mid = !major && v % 5 === 0
              return (
                <div
                  key={v}
                  className={`weight-tick${major ? ' major' : mid ? ' mid' : ''}`}
                  style={{ left: (v - MIN) * TICK_PX }}
                >
                  {major && <span className="weight-tick-label">{v}</span>}
                </div>
              )
            })}
          </motion.div>
          <div className="weight-dial-pointer" aria-hidden />
          <div className="weight-dial-fade weight-dial-fade-left" aria-hidden />
          <div className="weight-dial-fade weight-dial-fade-right" aria-hidden />
        </div>
      </div>
    </div>
  )
}

// Segmented pill toggle with a sliding selected indicator. Shared by both
// measurement inputs so the toggle feels like one consistent control.
function UnitToggle({
  groupId,
  options,
  value,
  onChange,
}: {
  groupId: string
  options: readonly (readonly [string, string])[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="unit-toggle" role="tablist">
      {options.map(([v, label]) => {
        const selected = value === v
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`unit-toggle-opt${selected ? ' selected' : ''}`}
            onClick={() => onChange(v)}
          >
            {selected && (
              <motion.span
                layoutId={`unit-toggle-indicator-${groupId}`}
                className="unit-toggle-indicator"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
            <span className="unit-toggle-label">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
